/**
 * Volume payload decoding: zip inflate + NPY parse + segmentation label pass.
 *
 * This is the work that used to run inline on the analysis page and froze it for
 * 20+ s (#670). A 256³ scan with a segmentation and a CT attached inflates to
 * ~150 MB (`seg_rgba.npy` and the CT overlay are 4 bytes per voxel), and the
 * per-voxel label pass adds another 16M-iteration loop on top.
 *
 * The module is deliberately free of vtk.js and DOM references so the decode
 * worker can import it. `unzipSync` stays the inflate implementation: inside a
 * worker, blocking a thread nobody waits on is the point.
 */
import { Npyjs } from '@brainfocus/contrib/npyjs';
import { assert } from '@brainfocus/web/utils/assert';
import { SegLabels, rgbaToSegLabels } from '@brainfocus/web/domain/neuroimage/segmentation_labels';
import * as fflate from 'fflate';

import type { OverlayVolumeData, OverlayVolumes } from '@brainfocus/web/domain/neuroimage/overlay_roles';

import type { Brain3dMetadata } from '@brainfocus/web/domain/neuroimage/brain_3d';

/** A decoded volume payload, in the shape the viewer consumes. */
export interface DecodedVolume {
    metadata: Brain3dMetadata;
    /** uint8 (0–255) for MRI, or int16 real Hounsfield Units for CT (#501). */
    mri: Uint8Array | Int16Array;
    stlData?: ArrayBuffer;
    /**
     * Segmentation overlay with the per-voxel RGBA→label pass already applied.
     * Replaces shipping raw RGBA to the main thread: the labels are 1–2 bytes
     * per voxel instead of 4, and the loop is already paid.
     */
    segLabels?: SegLabels;
    /**
     * Overlay volumes coregistered to the MRI, by role (#502 CT, #989 MRI), on the MRI grid.
     * Colored in the viewers: the pre-colored CT texture this used to carry was never passed
     * on to them, so computing it here only cost worker time.
     */
    overlays: OverlayVolumes;
}

/**
 * Decode a volume payload zip.
 *
 * Runs on whichever thread calls it — the worker in the normal path, the main
 * thread in the fallback. Throws on a malformed archive, which the caller turns
 * into a rejected load.
 *
 * @param payload Raw zip bytes as returned by the volume endpoints.
 */
export function decodeVolumePayloadSync(payload: ArrayBuffer): DecodedVolume {
    const unzipd = fflate.unzipSync(new Uint8Array(payload));
    assert(unzipd['mri.npy'], `Missing file: mri.npy`);
    assert(unzipd['metadata.json'], `Missing file: metadata.json`);

    const npyjs = new Npyjs();
    const mriNpy = npyjs.parse<Uint8Array | Int16Array>(entryBuffer(unzipd['mri.npy']));
    const metadata = JSON.parse(new TextDecoder().decode(unzipd['metadata.json'])) as Brain3dMetadata;
    const brainStl = unzipd['brain.stl'];

    let segLabels: SegLabels | undefined;
    if (unzipd['seg_rgba.npy']) {
        const segRgba = npyjs.parse<Uint8Array>(entryBuffer(unzipd['seg_rgba.npy'])).data;
        segLabels = rgbaToSegLabels(segRgba);
    }

    const overlays: OverlayVolumes = {};
    for (const entry of metadata.overlays ?? []) {
        const file = unzipd[entry.file];
        if (!file) continue;
        overlays[entry.role] = npyjs.parse<OverlayVolumeData>(entryBuffer(file)).data;
    }

    return {
        metadata,
        mri: mriNpy.data,
        stlData: brainStl ? entryBuffer(brainStl) : undefined,
        segLabels,
        overlays,
    };
}

/**
 * Every ArrayBuffer in a decoded volume, for `postMessage`'s transfer list.
 *
 * Transferring rather than copying is what keeps the boundary crossing free of
 * a second volume-sized allocation. Duplicates are filtered: two arrays parsed
 * out of the same zip entry would otherwise be listed twice, which throws.
 */
export function decodedVolumeTransferables(volume: DecodedVolume): ArrayBuffer[] {
    const buffers = [
        volume.mri.buffer,
        volume.segLabels?.labels.buffer,
        ...Object.values(volume.overlays ?? {}).map(data => data?.buffer),
        volume.stlData,
    ].filter((buffer): buffer is ArrayBuffer => !!buffer);
    return Array.from(new Set(buffers));
}

/**
 * The entry's bytes as a standalone ArrayBuffer.
 *
 * `Npyjs.parse` returns a view into the buffer it is handed, and the result is
 * later transferred, so the buffer must belong to this entry alone. fflate
 * hands back exact-size arrays in practice; the slice is the safety net for
 * when it does not, and is skipped in the common case rather than copying a
 * volume for nothing.
 */
function entryBuffer(entry: Uint8Array): ArrayBuffer {
    if (entry.byteOffset === 0 && entry.byteLength === entry.buffer.byteLength) {
        return entry.buffer as ArrayBuffer;
    }
    return entry.buffer.slice(entry.byteOffset, entry.byteOffset + entry.byteLength) as ArrayBuffer;
}
